import React, { useState, useEffect, useRef, useId } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Search, CheckCircle2, AlertCircle, FlaskConical, Network, Loader2, Building2, X, Plus, MapPin } from "lucide-react";
import { getDiagnosticChains, sanitizePlace, validatePlace } from "../../services/diagnosticLabsService";
import { lookupPincodeDetail } from "../../services/hospitalService";

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const round6 = (n) => Math.round(Number(n) * 1e6) / 1e6;

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let gmapsPromise = null;
const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (gmapsPromise) return gmapsPromise;
  gmapsPromise = new Promise((resolve, reject) => {
    if (!GMAPS_KEY) { reject(new Error("missing-key")); return; }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.google?.maps ? resolve(window.google.maps) : reject(new Error("load-failed")));
    s.onerror = () => reject(new Error("load-failed"));
    document.head.appendChild(s);
  });
  return gmapsPromise;
};

/* ----------------------- module tab bar (Labs | Chains) ------------------ */
const DIAGNOSTIC_TABS = [
  { key: "labs", label: "Labs", icon: FlaskConical, to: "/subscriber-wallet/labs/labs" },
  { key: "chains", label: "Chains", icon: Network, to: "/subscriber-wallet/labs" },
];

export const DiagnosticTabs = ({ active }) => {
  const navigate = useNavigate();
  return (
    <div className="border-b border-gray-200">
      <nav className="flex gap-1 -mb-px" aria-label="Diagnostic sections">
        {DIAGNOSTIC_TABS.map((t) => {
          const Icon = t.icon;
          const selected = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => !selected && navigate(t.to)}
              aria-current={selected ? "page" : undefined}
              className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                selected
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
              }`}
            >
              <Icon className={`w-4 h-4 ${selected ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"}`} />
              {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

/* ----------------------------- status badges ---------------------------- */
const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  inactive: "bg-amber-50 text-amber-700 ring-amber-600/20",
  archived: "bg-slate-100 text-slate-600 ring-slate-500/20",
};
const STATUS_DOT = {
  active: "bg-emerald-500",
  inactive: "bg-amber-500",
  archived: "bg-slate-400",
};

export const StatusBadge = ({ status }) => {
  const key = (status || "active").toLowerCase();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${
        STATUS_STYLE[key] || STATUS_STYLE.active
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[key] || STATUS_DOT.active}`} />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
};

const VERIFICATION_STYLE = {
  pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
  verified: "bg-blue-50 text-blue-700 ring-blue-600/20",
  rejected: "bg-red-50 text-red-700 ring-red-600/20",
};
const VERIFICATION_DOT = {
  pending: "bg-amber-500",
  verified: "bg-blue-500",
  rejected: "bg-red-500",
};

export const VerificationBadge = ({ status }) => {
  const key = (status || "pending").toLowerCase();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${
        VERIFICATION_STYLE[key] || VERIFICATION_STYLE.pending
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${VERIFICATION_DOT[key] || VERIFICATION_DOT.pending}`} />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
};

const TYPE_STYLE = {
  online: "bg-blue-50 text-blue-700 ring-blue-600/20",
  offline: "bg-slate-100 text-slate-600 ring-slate-500/20",
};
const TYPE_DOT = { online: "bg-blue-500", offline: "bg-slate-400" };

export const TypeBadge = ({ type }) => {
  const key = (type || "offline").toLowerCase();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${
        TYPE_STYLE[key] || TYPE_STYLE.offline
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${TYPE_DOT[key] || TYPE_DOT.offline}`} />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
};

/* ------------------------------- stat card ------------------------------ */
export const StatCard = ({ icon: Icon, label, value, accent, loading, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: index * 0.06, ease: "easeOut" }}
    className="relative overflow-hidden bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <div className="mt-2 h-9 flex items-end">
          {loading ? (
            <div className="h-7 w-12 rounded-md bg-gray-100 animate-pulse" />
          ) : (
            <span className="text-3xl font-bold text-gray-900 leading-none">{value}</span>
          )}
        </div>
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.bg} ${accent.fg}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${accent.bar}`} />
  </motion.div>
);

/* --------------------------- segmented control --------------------------- */
export const SegmentedControl = ({ options, value, onChange, size = "md", disabled = false }) => {
  const pad = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  return (
    <div className={`inline-flex flex-wrap p-1 bg-gray-100 rounded-lg gap-1 ${disabled ? "opacity-80" : ""}`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            className={`${pad} font-medium rounded-md transition-all ${
              selected ? "bg-white text-gray-900 shadow-sm" : `text-gray-500 ${disabled ? "" : "hover:text-gray-700"}`
            } ${disabled ? "cursor-default" : ""}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/* --------------------------- chip multi-select --------------------------- */
export const ChipMultiSelect = ({ options, value = [], onChange, searchable = false, creatable = false, commonCount = 0, placeholder = "Search…", disabled = false }) => {
  const [query, setQuery] = useState("");
  const labelOf = (v) => options.find((o) => o.value === v)?.label || v;

  // View mode — read-only pills (handles custom values not present in options).
  if (disabled) {
    return value.length ? (
      <div className="flex flex-wrap gap-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-blue-50 border border-blue-200 text-blue-700">
            <Check className="w-3.5 h-3.5" /> {labelOf(v)}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-400">—</p>
    );
  }

  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  const q = query.trim();
  let visible;
  if (!searchable) {
    visible = options;
  } else if (q) {
    visible = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  } else if (commonCount) {
    // Collapsed: show the first few common options + any selected ones beyond them.
    visible = options.filter((o, i) => i < commonCount || value.includes(o.value));
  } else {
    visible = options;
  }
  const moreHidden = searchable && !q && commonCount && options.length > visible.length;

  // Selected values not in `options` (e.g. user-added languages) — always shown so they can be removed.
  const optionValues = options.map((o) => o.value);
  const customSelected = value.filter((v) => !optionValues.includes(v));

  const canCreate =
    creatable && !!q &&
    !options.some((o) => o.label.toLowerCase() === q.toLowerCase() || String(o.value).toLowerCase() === q.toLowerCase()) &&
    !value.some((v) => String(v).toLowerCase() === q.toLowerCase());

  const addCustom = () => { onChange([...value, q]); setQuery(""); };

  return (
    <div>
      {searchable && (
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (canCreate && e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder={creatable ? "Search or add…" : placeholder}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
          />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {visible.map((opt) => {
          const selected = value.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all ${
                selected
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              {selected && <Check className="w-3.5 h-3.5" />}
              {opt.label}
            </button>
          );
        })}
        {customSelected.map((v) => (
          <button key={v} type="button" onClick={() => toggle(v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border bg-blue-50 border-blue-300 text-blue-700">
            <Check className="w-3.5 h-3.5" /> {v}
          </button>
        ))}
        {canCreate && (
          <button type="button" onClick={addCustom}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 transition-all">
            <Plus className="w-3.5 h-3.5" /> Add “{q}”
          </button>
        )}
        {visible.length === 0 && customSelected.length === 0 && !canCreate && (
          <p className="text-sm text-gray-400 py-1">No matches</p>
        )}
      </div>
      {moreHidden ? <p className="text-xs text-gray-400 mt-1.5">Type to search {options.length - visible.length} more…</p> : null}
    </div>
  );
};

/* -------------------------------- toast --------------------------------- */
export const Toast = ({ toast }) =>
  toast ? (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
        toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
      }`}
    >
      {toast.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {toast.message}
    </motion.div>
  ) : null;

export const getCurrentRole = () => {
  const authRole = localStorage.getItem("authRole");
  return {
    wallet_superadmin: "SuperAdmin",
    wallet_finance: "Finance",
    wallet_ops: "Ops",
    wallet_support: "Support",
  }[authRole] || null;
};

/* --------------------- async chain typeahead (shared) -------------------- */
// Relates a lab to a chain. value = chain id (or null = standalone), label =
// chain brand name; onChange(id|null, label).
export const AsyncChainSelect = ({ role, value, label, onChange, placeholder = "Search chains…", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debRef = useRef(null);
  const listboxId = useId();

  useEffect(() => {
    const onDocMouseDown = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await getDiagnosticChains(role, { items: 8, search: query });
        setResults(resp?.msg?.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query, open, role]);

  // View mode — read-only chain (or "Standalone"), no clear/search.
  if (disabled) {
    return value ? (
      <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg">
        <span className="w-7 h-7 rounded-lg bg-white text-blue-600 flex items-center justify-center shrink-0">
          <Building2 className="w-3.5 h-3.5" />
        </span>
        <span className="text-sm font-medium text-gray-800 truncate">{label || "Chain"}</span>
      </div>
    ) : (
      <p className="text-sm text-gray-400">Standalone — not part of any chain.</p>
    );
  }

  // Selected state — a clearable chip showing the chain.
  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 border border-blue-200 bg-blue-50/60 rounded-lg">
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-white text-blue-600 flex items-center justify-center shrink-0">
            <Building2 className="w-3.5 h-3.5" />
          </span>
          <span className="text-sm font-medium text-blue-800 truncate">{label || "Selected chain"}</span>
        </span>
        <button
          type="button"
          onClick={() => onChange(null, "")}
          className="text-blue-400 hover:text-blue-700 transition-colors shrink-0"
          aria-label="Detach chain (make standalone)"
          title="Make standalone"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
      />
      {open && (
        <div id={listboxId} className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto" role="listbox">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">No chains found</div>
          ) : (
            results.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => { onChange(c.id, c.brand_name); setOpen(false); setQuery(""); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50/60 flex items-center gap-2 transition-colors"
              >
                <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate">{c.brand_name}</span>
                {c.active === false && <span className="ml-auto text-[10px] text-amber-600">inactive</span>}
              </button>
            ))
          )}
          <a
            href="/subscriber-wallet/labs"
            target="_blank"
            rel="noreferrer"
            className="block px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50/60 border-t border-gray-100 font-medium"
          >
            + Manage / create chains
          </a>
        </div>
      )}
    </div>
  );
};

/* ------------- pincode → city/state autofill (shared address) ------------ */
// Pincode-first address trio. Typing a 6-digit pincode looks up city + state
// from the India Post API and prefills them (still editable). onChange receives
// a partial patch, e.g. { pincode } or { city, state }.
const _addrInput =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-700 disabled:cursor-default";

const AddrLabel = ({ children }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1.5">{children}</label>
);

export const PincodeAutoAddress = ({
  pincode = "", locality = "", city = "", state = "", onChange, disabled = false,
  blur = null,
  fieldKeys = { pincode: "pincode", locality: "locality", city: "city", state: "state" },
}) => {
  const [status, setStatus] = useState(null); // 'loading' | 'found' | 'notfound'
  const [localities, setLocalities] = useState([]); // post-office names for the pincode
  const debRef = useRef(null);

  const cityErr = validatePlace(city, "City");
  const stateErr = validatePlace(state, "State");

  const lookup = async (pin, { autofill = true } = {}) => {
    setStatus("loading");
    try {
      const msg = await lookupPincodeDetail(pin);
      const locations = msg?.locations || [];
      if (locations.length) {
        // `city` is the locality (post-office) name; `district` is the city.
        const names = [...new Set(locations.map((l) => l.city).filter(Boolean))];
        setLocalities(names);

        if (autofill) {
          onChange({ city: locations[0].district || "", state: locations[0].state || "", locality: names[0] || "" });
        }
        setStatus("found");
      } else {
        setLocalities([]);
        setStatus("notfound");
      }
    } catch {
      setLocalities([]);
      setStatus("notfound");
    }
  };

  // without overwriting the saved city / state / locality.
  useEffect(() => {
    if (/^\d{6}$/.test(pincode)) lookup(pincode, { autofill: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPincode = (val) => {
    const pin = val.replace(/\D/g, "").slice(0, 6);
    onChange({ pincode: pin });
    setStatus(null);
    setLocalities([]);
    if (debRef.current) clearTimeout(debRef.current);
    if (pin.length === 6) debRef.current = setTimeout(() => lookup(pin), 250);
  };

  const cityKey = fieldKeys.city;
  const stateKey = fieldKeys.state;
  const cityInputCls = blur ? blur.errCls(cityKey, cityErr, _addrInput) : _addrInput;
  const stateInputCls = blur ? blur.errCls(stateKey, stateErr, _addrInput) : _addrInput;
  const showCityErr = blur ? blur.msg(cityKey, cityErr) : (!disabled && cityErr ? cityErr : "");
  const showStateErr = blur ? blur.msg(stateKey, stateErr) : (!disabled && stateErr ? stateErr : "");

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <AddrLabel>Pincode</AddrLabel>
          <div className="relative">
            <input
              className={`${_addrInput} pr-9`}
              disabled={disabled}
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit"
              value={pincode}
              onChange={(e) => onPincode(e.target.value)}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {status === "loading" && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
              {status === "found" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
            </span>
          </div>
        </div>
        <div>
          <AddrLabel>Locality</AddrLabel>
          {localities.length > 0 ? (
            <select className={_addrInput} disabled={disabled} value={locality} onChange={(e) => onChange({ locality: e.target.value })}>
              {locality && !localities.includes(locality) && <option value={locality}>{locality}</option>}
              {localities.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          ) : (
            <input className={_addrInput} disabled={disabled} value={locality} placeholder="Auto-fills from pincode" onChange={(e) => onChange({ locality: e.target.value })} />
          )}
        </div>
        <div>
          <AddrLabel>City</AddrLabel>
          <input
            className={cityInputCls}
            disabled={disabled}
            value={city}
            onChange={(e) => onChange({ city: sanitizePlace(e.target.value) })}
            onBlur={() => blur?.touch(cityKey)}
          />
          {showCityErr && <p className="mt-1 text-xs text-red-500">{showCityErr}</p>}
        </div>
        <div>
          <AddrLabel>State</AddrLabel>
          <input
            className={stateInputCls}
            disabled={disabled}
            value={state}
            onChange={(e) => onChange({ state: sanitizePlace(e.target.value) })}
            onBlur={() => blur?.touch(stateKey)}
          />
          {showStateErr && <p className="mt-1 text-xs text-red-500">{showStateErr}</p>}
        </div>
      </div>
      {!disabled && status === "loading" && (
        <p className="mt-2 text-xs text-blue-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Looking up pincode…</p>
      )}
      {!disabled && status === "found" && (
        <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Auto-filled locality, city &amp; state — pick your locality or edit if needed.</p>
      )}
      {!disabled && status === "notfound" && pincode.length === 6 && (
        <p className="mt-2 text-xs text-amber-600">Couldn’t find that pincode — enter locality, city &amp; state manually.</p>
      )}
    </div>
  );
};

// Google Maps with a draggable marker. The pin starts at the captured lat/long
// (the backend "Auto-fetch" fills these); drag it — or click anywhere — to the
// exact spot. onChange receives { latitude, longitude } (6-dp), feeding the same
// form fields as the editable inputs. Read-only when disabled.
export const LocationPicker = ({ latitude, longitude, onChange, disabled = false }) => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const dragListenerRef = useRef(null);
  const dragStartListenerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const disabledRef = useRef(disabled);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  disabledRef.current = disabled;
  const [loadError, setLoadError] = useState("");

  const numLat = Number(latitude);
  const numLng = Number(longitude);
  const hasCoords =
    latitude !== "" && latitude != null && longitude !== "" && longitude != null &&
    Number.isFinite(numLat) && Number.isFinite(numLng);

  const clearInteractivity = () => {
    dragStartListenerRef.current?.remove();
    dragStartListenerRef.current = null;
    dragListenerRef.current?.remove();
    dragListenerRef.current = null;
    clickListenerRef.current?.remove();
    clickListenerRef.current = null;
  };

  // Toggle drag/click handlers when disabled flips (e.g. lab detail view → edit).
  const syncInteractivity = () => {
    const map = mapRef.current;
    const marker = markerRef.current;
    const maps = window.google?.maps;
    if (!map || !maps) return;

    clearInteractivity();

    if (disabledRef.current) {
      marker?.setDraggable(false);
      return;
    }

    if (marker) {
      marker.setDraggable(true);
      dragStartListenerRef.current = marker.addListener("dragstart", () => {
        isDraggingRef.current = true;
      });
      dragListenerRef.current = marker.addListener("dragend", () => {
        isDraggingRef.current = false;
        const p = marker.getPosition();
        onChangeRef.current?.({ latitude: round6(p.lat()), longitude: round6(p.lng()) });
      });
    }

    clickListenerRef.current = map.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      ensureMarker(lat, lng);
      onChangeRef.current?.({ latitude: round6(lat), longitude: round6(lng) });
    });
  };

  // Create or move the marker; interactivity is wired separately via syncInteractivity.
  const ensureMarker = (lat, lng) => {
    const map = mapRef.current;
    const maps = window.google?.maps;
    if (!map || !maps) return;
    if (markerRef.current) {
      markerRef.current.setPosition({ lat, lng });
      return;
    }
    const marker = new maps.Marker({ position: { lat, lng }, map, draggable: false });
    markerRef.current = marker;
    syncInteractivity();
  };

  // Init once (after the SDK loads).
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapEl.current || mapRef.current) return;
        const center = hasCoords ? { lat: numLat, lng: numLng } : INDIA_CENTER;
        const map = new maps.Map(mapEl.current, {
          center, zoom: hasCoords ? 16 : 5,
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        });
        mapRef.current = map;
        if (hasCoords) ensureMarker(numLat, numLng);
        else syncInteractivity();
      })
      .catch((err) => !cancelled && setLoadError(err.message));
    return () => {
      cancelled = true;
      clearInteractivity();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncInteractivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Sync when coords change from outside (Auto-fetch button or editable fields).
  // Skip while the user is dragging — parent re-renders (e.g. pincode typing) would
  // otherwise snap the pin back to the last fetched lat/long before dragend fires.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !hasCoords || isDraggingRef.current) return;
    const cur = markerRef.current?.getPosition?.();
    if (cur && round6(cur.lat()) === round6(numLat) && round6(cur.lng()) === round6(numLng)) return;
    ensureMarker(numLat, numLng);
    map.setCenter({ lat: numLat, lng: numLng });
    if (map.getZoom() < 16) map.setZoom(16);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numLat, numLng, hasCoords]);

  if (loadError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
        <MapPin className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
        <p className="text-sm font-medium text-amber-700">Map unavailable</p>
        <p className="text-[11px] text-amber-600 mt-0.5">
          {loadError === "missing-key"
            ? "Set REACT_APP_GOOGLE_MAPS_API_KEY in .env and restart the dev server."
            : "Couldn’t load Google Maps — check the key, Maps JavaScript API & billing."}
        </p>
        {hasCoords && (
          <a href={`https://www.google.com/maps?q=${numLat},${numLng}`} target="_blank" rel="noreferrer"
            className="inline-block mt-2 text-[11px] text-blue-600 hover:underline">Open in Google Maps ↗</a>
        )}
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-200">
      <div ref={mapEl} style={{ height: 320, width: "100%" }} />
      {!disabled && (
        <div className="absolute bottom-2 left-2 z-[1] px-2.5 py-1 rounded-lg bg-white/90 shadow-sm text-[11px] font-medium text-gray-600 pointer-events-none">
          {hasCoords ? "Drag the pin to the exact spot" : "Tap the map to drop a pin"}
        </div>
      )}
    </div>
  );
};


